import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { openStorageFile } from "@/lib/openStorageFile";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Volume2, FileText, ExternalLink, Image as ImageIcon, Link2, File } from "lucide-react";
import { toast } from "sonner";

const kindIcon = { video: Play, audio: Volume2, pdf: FileText, image: ImageIcon, link: Link2, other: File };

export default function LearnerResources() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("all");
  const [scope, setScope] = useState("all");
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    const params = {};
    if (kind !== "all") params.kind = kind;
    if (scope !== "all") params.scope = scope;
    api.get("/learner/resources", { params }).then(({ data }) => setItems(data));
  }, [kind, scope]);

  const sorted = useMemo(() => {
    const list = [...items];
    const byTitle = (a, b) => (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" });
    if (sort === "az") list.sort(byTitle);
    else if (sort === "za") list.sort((a, b) => byTitle(b, a));
    else if (sort === "oldest") list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [items, sort]);

  const openResource = async (r) => {
    if (r.url) {
      window.open(r.url, "_blank", "noopener,noreferrer");
    } else if (r.file_path) {
      try { await openStorageFile(r.file_path); }
      catch (e) { toast.error(e.message || "Impossible d'ouvrir le fichier"); }
    }
  };

  return (
    <div className="lg:ml-56">
      <h1 className="font-serif-accent text-3xl sm:text-4xl">Ressources</h1>
      <p className="text-[#5C6561] mt-1 mb-5 text-sm">Ce que votre coach a préparé pour vous.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <Tabs value={kind} onValueChange={setKind}>
          <TabsList className="bg-[#F4EFEA] flex-wrap">
            <TabsTrigger value="all" data-testid="lr-kind-all">Toutes</TabsTrigger>
            <TabsTrigger value="video" data-testid="lr-kind-video">Vidéos</TabsTrigger>
            <TabsTrigger value="audio" data-testid="lr-kind-audio">Audio</TabsTrigger>
            <TabsTrigger value="pdf" data-testid="lr-kind-pdf">PDFs</TabsTrigger>
            <TabsTrigger value="image" data-testid="lr-kind-image">Images</TabsTrigger>
            <TabsTrigger value="link" data-testid="lr-kind-link">Liens</TabsTrigger>
            <TabsTrigger value="other" data-testid="lr-kind-other">Autres</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList className="bg-[#F4EFEA]">
            <TabsTrigger value="all">Tout</TabsTrigger>
            <TabsTrigger value="mine" data-testid="lr-scope-mine">Mes ressources</TabsTrigger>
            <TabsTrigger value="shared" data-testid="lr-scope-shared">Partagées</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full sm:w-48" data-testid="lr-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Plus récentes</SelectItem>
            <SelectItem value="oldest">Plus anciennes</SelectItem>
            <SelectItem value="az">Alphabétique (A → Z)</SelectItem>
            <SelectItem value="za">Alphabétique (Z → A)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {sorted.length === 0 && <div className="col-span-full dec-card p-6 text-center text-[#5C6561]">Aucune ressource pour le moment.</div>}
        {sorted.map(r => {
          const Icon = kindIcon[r.kind] || FileText;
          return (
            <button
              key={r.id}
              onClick={() => openResource(r)}
              className="text-left dec-card p-4 hover:border-[#D95D39]"
              data-testid={`learner-resource-${r.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg bg-[#FDF0EC] text-[#D95D39] flex items-center justify-center"><Icon className="w-4 h-4" /></div>
                <span className={`dec-pill text-xs px-2 py-0.5 ${r.visibility === "private" ? "status-inprogress" : "status-completed"}`}>
                  {r.visibility === "private" ? "Pour vous" : "Partagée"}
                </span>
              </div>
              <div className="mt-3 font-semibold">{r.title}</div>
              {r.description && <div className="text-xs text-[#5C6561] mt-1 line-clamp-2">{r.description}</div>}
              <div className="mt-3 text-xs text-[#D95D39] font-medium inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Ouvrir</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
